import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material'
import { Face, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { ParentAnswer, Poll, PollResults, PollType, RatingDisplay } from '@/types/NewsFeed'
import styles from '@/container/events/eventDay/Dialog/presenceDialog/presenceDialog.module.scss'
import rowStyles from '@/container/events/eventDay/Dialog/presenceDialog/userReservationEvent/userReservationEvent.module.scss'

interface PollAnswersDialogProps {
    open: boolean
    handleClose: () => void
    poll: Poll
    results: PollResults
}

export default function PollAnswersDialog({
    open,
    handleClose,
    poll,
    results
}: Readonly<PollAnswersDialogProps>): ReactElement {
    const { t } = useTranslation()
    const [expandedGroups, setExpandedGroups] = useState<number[]>([])
    const answers: ParentAnswer[] = poll.anonymous ? [] : (results.parentAnswers ?? [])
    const groups: { id: number; label: string; voters: ParentAnswer[] }[] =
        poll.type === PollType.rating
            ? Array.from({ length: 5 }, (_, index) => {
                  const rating: number = index + 1
                  const label: string =
                      poll.ratingDisplay === RatingDisplay.smileys
                          ? `${['😣', '😞', '😐', '😊', '😄'][index]} ${rating} / 5`
                          : poll.ratingDisplay === RatingDisplay.numbers
                            ? `${rating} / 5`
                            : `★ ${rating} / 5`
                  return { id: rating, label, voters: answers.filter((answer) => answer.rating === rating) }
              })
            : [...results.options]
                  .sort((a, b) => a.order - b.order)
                  .map((option) => ({
                      id: option.id,
                      label: option.text || `${t('pollOption')} ${option.order + 1}`,
                      voters: answers.filter((answer) => answer.optionIds?.includes(option.id))
                  }))

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('pollAnswersTitle')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.Container}>
                    <TableContainer>
                        {groups.map((group) => {
                            const expanded: boolean = expandedGroups.includes(group.id)
                            const toggleGroup: () => void = (): void => {
                                setExpandedGroups((current) =>
                                    expanded ? current.filter((id) => id !== group.id) : [...current, group.id]
                                )
                            }
                            return (
                                <div key={group.id} className={rowStyles.TablePlacement}>
                                    <div
                                        className={rowStyles.TitleContainer}
                                        role='button'
                                        tabIndex={0}
                                        aria-expanded={expanded}
                                        onClick={toggleGroup}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault()
                                                toggleGroup()
                                            }
                                        }}
                                    >
                                        <div className={rowStyles.Title}>
                                            <p className={rowStyles.Text}>{group.label}</p>
                                            {expanded ? (
                                                <KeyboardArrowUp className={rowStyles.Icon} />
                                            ) : (
                                                <KeyboardArrowDown className={rowStyles.Icon} />
                                            )}
                                        </div>
                                        {!expanded && (
                                            <div className={rowStyles.IndicatorsContainer}>
                                                <div className={rowStyles.Indicator}>
                                                    <Face className={rowStyles.Icon} />
                                                    {group.voters.length}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {expanded && (
                                        <Table className={rowStyles.Table}>
                                            <TableBody>
                                                {group.voters.length > 0 ? (
                                                    [...group.voters]
                                                        .sort((a, b) => a.parentName.localeCompare(b.parentName))
                                                        .map((answer, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell sx={{ width: '20%' }} align='center'>
                                                                    <Face
                                                                        sx={{
                                                                            color: 'var(--daycare-dark)',
                                                                            marginRight: '20px',
                                                                            padding: '0 10px'
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ width: '80%' }}>
                                                                    {answer.parentName.trim() || t('unknown')}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell>{t('noAnswers')}</TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </div>
                            )
                        })}
                    </TableContainer>
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type='close' onClick={handleClose} className={styles.Button} />
                </div>
            </DialogBody>
        </Dialog>
    )
}
